// Fleet — shared maintenance modals for BOTH VehicleDetail and EquipmentDetail
// (Equipment Detail CP). VehicleDetail defined these inline; now that the two
// pages are siblings, the modals are extracted here and parameterized by a
// `target` discriminator. The extraction is clean: the schedule form, the
// service-record form, the roll-forward, and the modal shell are identical
// across targets — only three things differ, all driven off `target`:
//   1. which FK column the row carries (vehicle_id vs equipment_id),
//   2. the mileage interval option — HIDDEN for equipment (the
//      maintenance_schedules CHECK enforces `interval_type='mileage' → vehicle`,
//      so the UI must not offer it), and
//   3. the odometer field + vehicle_odometer_log write — vehicle-only.
// DueBadge lives here too since both pages' schedule lists render it.
import React, { useState, useEffect } from 'react'
import { useMutation } from '@tanstack/react-query'
import { supabase } from '@/lib/supabase'
import { useAuth } from '@/lib/auth'
import { toast } from 'sonner'
import { field, lbl, ModalShell } from './FleetPage'
import { rollForward, type MaintenanceSchedule, type DueState } from './maintenanceUtils'

// Target discriminator — a modal instance is bound to exactly one asset.
export type MaintTarget = { vehicleId: string } | { equipmentId: string }
export const isVehicleTarget = (t: MaintTarget): t is { vehicleId: string } => 'vehicleId' in t
// The FK column patch a given target inserts against.
const targetFk = (t: MaintTarget): { vehicle_id: string } | { equipment_id: string } =>
  isVehicleTarget(t) ? { vehicle_id: t.vehicleId } : { equipment_id: t.equipmentId }

export function DueBadge({ state }: { state: DueState }) {
  if (state === 'ok') return null
  const overdue = state === 'overdue'
  return <span style={{ fontSize: 10, fontWeight: 600, color: overdue ? 'var(--danger)' : 'var(--warning)', background: overdue ? 'var(--danger-dim)' : 'var(--warning-dim)', padding: '2px 7px', borderRadius: 100 }}>{overdue ? 'Overdue' : 'Due soon'}</span>
}

// ─── ScheduleModal — mileage option hidden for equipment targets ────────────
export function ScheduleModal({ open, onClose, onSaved, workspaceId, target, editing }: {
  open: boolean; onClose: () => void; onSaved: () => void; workspaceId: string | null; target: MaintTarget; editing: MaintenanceSchedule | null
}) {
  const allowMileage = isVehicleTarget(target)
  const [title, setTitle] = useState('')
  const [type, setType] = useState<'date' | 'mileage'>('date')
  const [days, setDays] = useState(''); const [miles, setMiles] = useState('')
  const [nextDate, setNextDate] = useState(''); const [nextMiles, setNextMiles] = useState('')
  const [active, setActive] = useState(true); const [notes, setNotes] = useState('')

  useEffect(() => {
    if (!open) return
    if (editing) {
      // Equipment schedules are always date-based (CHECK-enforced); a stray
      // mileage value can't occur, but clamp to 'date' when mileage isn't allowed.
      setTitle(editing.title); setType(allowMileage ? editing.interval_type : 'date')
      setDays(editing.interval_days != null ? String(editing.interval_days) : ''); setMiles(editing.interval_miles != null ? String(editing.interval_miles) : '')
      setNextDate(editing.next_due_date ?? ''); setNextMiles(editing.next_due_miles != null ? String(editing.next_due_miles) : '')
      setActive(editing.active); setNotes(editing.notes ?? '')
    } else {
      setTitle(''); setType('date'); setDays(''); setMiles(''); setNextDate(''); setNextMiles(''); setActive(true); setNotes('')
    }
  }, [open, editing, allowMileage])

  const save = useMutation({
    mutationFn: async () => {
      if (!workspaceId) throw new Error('Not signed in')
      if (!title.trim()) throw new Error('Title is required')
      const effectiveType = allowMileage ? type : 'date'
      const payload = {
        title: title.trim(), interval_type: effectiveType,
        interval_days: effectiveType === 'date' && days.trim() ? parseInt(days, 10) : null,
        interval_miles: effectiveType === 'mileage' && miles.trim() ? parseInt(miles, 10) : null,
        next_due_date: effectiveType === 'date' ? (nextDate || null) : null,
        next_due_miles: effectiveType === 'mileage' && nextMiles.trim() ? parseInt(nextMiles, 10) : null,
        active, notes: notes.trim() || null,
      }
      if (editing) { const { error } = await supabase.from('maintenance_schedules').update(payload).eq('id', editing.id); if (error) throw error }
      else { const { error } = await supabase.from('maintenance_schedules').insert({ workspace_id: workspaceId, ...targetFk(target), ...payload }); if (error) throw error }
    },
    onSuccess: () => onSaved(), onError: (e: any) => toast.error(e?.message || 'Failed to save schedule'),
  })
  const del = useMutation({
    mutationFn: async () => { if (!editing) return; const { error } = await supabase.from('maintenance_schedules').delete().eq('id', editing.id); if (error) throw error },
    onSuccess: () => onSaved(), onError: (e: any) => toast.error(e?.message || 'Failed to delete schedule'),
  })

  return (
    <ModalShell open={open} onClose={onClose} title={editing ? 'Edit schedule' : 'Add schedule'}
      onDelete={editing ? () => { if (window.confirm('Delete this schedule?')) del.mutate() } : undefined}
      canSave={!!title.trim() && !save.isPending} saving={save.isPending} saveLabel={editing ? 'Save' : 'Add'} onSave={() => save.mutate()}>
      <div><label style={lbl}>Title</label><input value={title} onChange={e => setTitle(e.target.value)} autoFocus placeholder="Oil change" style={{ ...field, fontWeight: 600 }} /></div>
      {allowMileage && (
        <div><label style={lbl}>Interval type</label>
          <select value={type} onChange={e => setType(e.target.value as 'date' | 'mileage')} style={{ ...field, cursor: 'pointer' }}>
            <option value="date">Date-based</option><option value="mileage">Mileage-based</option>
          </select>
        </div>
      )}
      {(!allowMileage || type === 'date') ? (
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
          <div><label style={lbl}>Every (days)</label><input value={days} onChange={e => setDays(e.target.value.replace(/[^0-9]/g, ''))} inputMode="numeric" placeholder="90" style={field} /></div>
          <div><label style={lbl}>Next due date</label><input type="date" value={nextDate} onChange={e => setNextDate(e.target.value)} style={{ ...field, colorScheme: 'dark' }} /></div>
        </div>
      ) : (
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
          <div><label style={lbl}>Every (miles)</label><input value={miles} onChange={e => setMiles(e.target.value.replace(/[^0-9]/g, ''))} inputMode="numeric" placeholder="5000" style={field} /></div>
          <div><label style={lbl}>Next due (odometer)</label><input value={nextMiles} onChange={e => setNextMiles(e.target.value.replace(/[^0-9]/g, ''))} inputMode="numeric" placeholder="55000" style={field} /></div>
        </div>
      )}
      <label style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer' }}>
        <input type="checkbox" checked={active} onChange={e => setActive(e.target.checked)} style={{ width: 15, height: 15, accentColor: 'var(--accent)' }} />
        <span style={{ fontSize: 13, color: 'var(--ink-secondary)' }}>Active</span>
      </label>
      <div><label style={lbl}>Notes</label><textarea value={notes} onChange={e => setNotes(e.target.value)} rows={2} style={{ ...field, resize: 'vertical' }} /></div>
    </ModalShell>
  )
}

// ─── RecordModal — log a service; roll a linked schedule forward ────────────
// Odometer field + the vehicle_odometer_log write are vehicle-only (equipment
// has no odometer concept). The record insert + roll-forward are target-agnostic.
export function RecordModal({ open, onClose, onSaved, workspaceId, target, schedules, defaultOdometer }: {
  open: boolean; onClose: () => void; onSaved: () => void; workspaceId: string | null; target: MaintTarget
  schedules: MaintenanceSchedule[]; defaultOdometer?: number | null
}) {
  const isVehicle = isVehicleTarget(target)
  const { profile } = useAuth()
  const [performedAt, setPerformedAt] = useState('')
  const [description, setDescription] = useState('')
  const [cost, setCost] = useState(''); const [odo, setOdo] = useState('')
  const [performedBy, setPerformedBy] = useState(''); const [scheduleId, setScheduleId] = useState('')

  useEffect(() => {
    if (!open) return
    setPerformedAt(new Date().toISOString().slice(0, 10))
    setDescription(''); setCost(''); setOdo(defaultOdometer != null ? String(defaultOdometer) : ''); setPerformedBy(''); setScheduleId('')
  }, [open, defaultOdometer])

  const save = useMutation({
    mutationFn: async () => {
      if (!workspaceId) throw new Error('Not signed in')
      if (!description.trim()) throw new Error('Description is required')
      const odoVal = isVehicle && odo.trim() ? parseInt(odo, 10) : null
      const { error: recErr } = await supabase.from('maintenance_records').insert({
        workspace_id: workspaceId, ...targetFk(target), schedule_id: scheduleId || null,
        performed_at: performedAt, description: description.trim(),
        cost: cost.trim() ? parseFloat(cost) : null, odometer_at_service: odoVal, performed_by: performedBy.trim() || null,
      })
      if (recErr) throw recErr

      // Vehicle only: a captured reading also logs to vehicle_odometer_log
      // (source='service'); the DB trigger bumps vehicles.odometer monotonically.
      if (isVehicle && odoVal != null) {
        const { error: odoErr } = await supabase.from('vehicle_odometer_log').insert({ workspace_id: workspaceId, vehicle_id: (target as { vehicleId: string }).vehicleId, reading: odoVal, source: 'service', recorded_by: profile?.id ?? null })
        if (odoErr) throw odoErr
      }

      // Roll the linked schedule forward (date or mileage) — target-agnostic;
      // equipment schedules are date-only so the odometer arg is ignored.
      if (scheduleId) {
        const sc = schedules.find(s => s.id === scheduleId)
        if (sc) {
          const patch = rollForward(sc, performedAt, odoVal)
          const { error: schErr } = await supabase.from('maintenance_schedules').update(patch).eq('id', scheduleId)
          if (schErr) throw schErr
        }
      }
    },
    onSuccess: () => onSaved(), onError: (e: any) => toast.error(e?.message || 'Failed to log service'),
  })

  return (
    <ModalShell open={open} onClose={onClose} title="Log service"
      canSave={!!description.trim() && !save.isPending} saving={save.isPending} saveLabel="Log" onSave={() => save.mutate()}>
      <div><label style={lbl}>Description</label><input value={description} onChange={e => setDescription(e.target.value)} autoFocus placeholder="Oil + filter change" style={{ ...field, fontWeight: 600 }} /></div>
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
        <div><label style={lbl}>Performed</label><input type="date" value={performedAt} onChange={e => setPerformedAt(e.target.value)} style={{ ...field, colorScheme: 'dark' }} /></div>
        <div><label style={lbl}>Cost ($)</label><input value={cost} onChange={e => setCost(e.target.value.replace(/[^0-9.]/g, ''))} inputMode="decimal" placeholder="0.00" style={field} /></div>
      </div>
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
        {isVehicle && <div><label style={lbl}>Odometer</label><input value={odo} onChange={e => setOdo(e.target.value.replace(/[^0-9]/g, ''))} inputMode="numeric" style={field} /></div>}
        <div><label style={lbl}>Performed by</label><input value={performedBy} onChange={e => setPerformedBy(e.target.value)} placeholder="Shop or tech" style={field} /></div>
      </div>
      <div><label style={lbl}>Against schedule (optional)</label>
        <select value={scheduleId} onChange={e => setScheduleId(e.target.value)} style={{ ...field, cursor: 'pointer' }}>
          <option value="">Ad-hoc (no schedule)</option>
          {schedules.filter(s => s.active).map(s => <option key={s.id} value={s.id}>{s.title}</option>)}
        </select>
      </div>
    </ModalShell>
  )
}
